import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { normalizeInternalAppRoute } from '../utils/internal_route.util';

@Injectable({ providedIn: 'root' })
export class AuthStateService {
    private readonly redirectKey = 'cb4.auth.redirectUrl';
    private readonly inactivityKey = 'cb4.auth.inactivityUrl';
    private currentUrl = '/';

    constructor(private router: Router) {
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event) => {
                this.currentUrl = event.urlAfterRedirects || '/';
            });
    }

    getCurrentUrl(): string {
        return this.currentUrl;
    }

    captureRequestedUrl(url: string): void {
        const target = this.normalizeInternalRedirect(url);
        if (!target) {
            return;
        }
        sessionStorage.setItem(this.redirectKey, target);
    }

    captureReturnUrl(route: ActivatedRouteSnapshot): void {
        const queryReturnUrl = route.queryParamMap.get('returnUrl');
        if (queryReturnUrl) {
            this.captureRequestedUrl(queryReturnUrl);
            return;
        }

        const stateReturnUrl = typeof history !== 'undefined'
            ? history.state?.returnUrl as string | undefined
            : undefined;
        if (stateReturnUrl) {
            this.captureRequestedUrl(stateReturnUrl);
        }
    }

    consumeRequestedUrl(): string | null {
        const value = sessionStorage.getItem(this.redirectKey);
        if (value) {
            sessionStorage.removeItem(this.redirectKey);
        }
        return this.normalizeInternalRedirect(value);
    }

    setInactivityRedirect(url: string): void {
        const target = this.normalizeInternalRedirect(url);
        if (!target) {
            return;
        }
        sessionStorage.setItem(this.inactivityKey, target);
    }

    consumeInactivityRedirect(): string | null {
        const value = sessionStorage.getItem(this.inactivityKey);
        if (value) {
            sessionStorage.removeItem(this.inactivityKey);
        }
        return this.normalizeInternalRedirect(value);
    }

    clearAllRedirects(): void {
        sessionStorage.removeItem(this.redirectKey);
        sessionStorage.removeItem(this.inactivityKey);
    }

    resolvePostLoginRedirect(defaultRoute: string): string {
        const requested = this.consumeRequestedUrl();
        if (requested) {
            return requested;
        }

        const inactivity = this.consumeInactivityRedirect();
        if (inactivity) {
            return inactivity;
        }

        return defaultRoute;
    }

    private normalizeInternalRedirect(url: string | null | undefined): string | null {
        return normalizeInternalAppRoute(url);
    }
}
